import React, { useEffect, useCallback } from "react";
import "./App.css";
import { Route, Routes, useLocation } from "react-router-dom";
import { connect, useSelector, useDispatch } from "react-redux";
import { SnackbarProvider } from "notistack";
import PrivateRoute from "@components/Routing/PrivateRoute";
import "@services/apiInterceptor"; // Initialize API interceptor
import setAuthToken from "@services/setAuthToken";
import { ErrorBoundary } from "@shared/components";
import { DivisionGroup } from "./features/shared";
import { Login } from "./features/auth";
import { Sidebar, NavigationProvider } from "./features/navigation";
import { HomepagePage as Homepage } from "./features/homepage";
import { MonthlyTourPlanPage as MonthlyTourPlan, PbiDashboard } from "./features/monthlyTourPlan";
import DRMplanning from "./Pages/DRMplanning";
import FeedbackForm from "./Components/DRMPlanning/FeedbackForm";
import DRMAutoRouter from "./Components/DRMPlanning/DRMAutoRouter";
import AttendanceForm from "./Components/DRMPlanning/AttendanceForm";
import DRMThankYou from "./Components/DRMPlanning/DRMThankYou";
import { MyDealerPage as MyDealer } from "./features/myDealer";
import { MyTeamPage as MyTeam } from "./features/myTeam";
import { MyDiaryPage as MyDiary } from "./features/myDiary";
import { DealerLocatorPage as DealerLocator } from "./features/dealerLocator";
import BottomNavbar from "@components/BottomNavbar";
import { KeyInformationPage as KeyInformation } from "./features/keyInformation";
import { CurrentDayTourPage as CurrentDayTour } from "./features/currentDayTour";
import { TotalFeedback } from "./features/myDiary";
import { MarketWorkingPage as MarketWorking } from "./features/marketWorking";
import { QueriesPage as Queries } from "./features/serviceRequest";
import { ExceptionDealerPage as ExceptionDealer } from "./features/exceptionDealer";
import { CWPPage as CWP } from "./features/cwp";
import { SurveyPage as Survey } from "./features/survey";
import { PowerBiReport as PowerBi } from "./features/powerBi";
import PastSrInsights from "@components/MarketWorking/PastSrInsights";
import { QueryDetails } from "./features/serviceRequest";
import { MarketVisitReportPage as MarketVisitReport } from "./features/marketVisitReport";
import Alert from "@components/Common/Alert";
import OnlineStatus from "@components/Common/OnlineStatus";
import { MyUserPage as MyUser } from "./features/myUser";
import packageJson from "../package.json";
import { fetchAllMasters, logout } from "@redux/actions/utility-actions";
import { MyProfilePage as MyProfile } from "./features/profile";
import { MyWssPage } from "./features/myWss";
import { PILSurveyPage } from "./features/pilSurvey";
import DRM from "./Pages/DRM";
import { PCMSPage as PCMS } from "./features/pcms";
import { TSIMarketWorking, KeyDealerPerformance, DistributorInvoicing, BranchPerformanceIndex, PILSimilarSurvey } from "./features/powerBi";
import { useIsAuthenticated } from "@azure/msal-react";
import DRMDetails from "./Components/DRMDetails/DRMDetails";
import DRMMeetingApproval from "./Components/DRMDetails/DRMMeetingApproval";

import { LCDFormPage as LCDForm } from "./features/lcdForm";
import OAAPPage from "./features/oaap";
import JcReviewPage from "./features/jcReview";
import { NotificationDrawer } from "@shared/components";
import { fetchNotificationsStart, fetchNotificationsSuccess, fetchNotificationsFailure } from "@redux/slices/oaapSlice";
import { getNotificationsApi } from "@services/oaap/notificationsApi";
import { getComplianceStatusApi } from "@services/oaap/complianceApi";
import { getNotificationsApi as getJcNotificationsApi } from "@services/jcReview/notificationsApi";
import { getComplianceStatusApi as getJcComplianceStatusApi } from "@services/jcReview/complianceApi";
import { useNavigationContext } from "./features/navigation/context/NavigationContext";

/** Thin wrapper so NotificationDrawer can consume NavigationContext */
const NotificationDrawerContainer = () => {
  const { isNotifDrawerOpen, closeNotifDrawer } = useNavigationContext();
  return <NotificationDrawer open={isNotifDrawerOpen} onClose={closeNotifDrawer} />;
};

function App({ logout }) {
  const dispatch = useDispatch();
  const location = useLocation();

  // Redux Selectors
  const lastFetchedAt = useSelector(
    (state) => state.masterFetchStatus?.lastFetchedAt,
  );
  const showDivisionGroupModal = useSelector(
    (state) => state.uiModal?.showDivisionGroupModal,
  );
  const accessToken = useSelector((state) => state?.tokens?.accessToken);
  const isAuthenticated = useSelector(
    (state) => state?.tokens?.isAuthenticated,
  );
  const isSSOAuthenticated = useIsAuthenticated();
  const userCode = useSelector((state) => state?.userDetails?.details?.Id || "");
  const userType = useSelector((state) =>
    state?.userType?.type !== "Other" ? state?.userType?.type : state?.userDetails?.details?.Title
  );
  const userDivision = useSelector((state) => state?.userDetails?.details?.Division || null);

  // Fetch OAAP + BM-TSI JC Review notifications and compliance status globally on login,
  // merged into a single bell feed. Each notification is tagged with `module` ("oaap" or "jc")
  // so the drawer knows which page to navigate to and which API to call on mark-read.
  useEffect(() => {
    const publicDRMRoutes = ['/login', '/DRMAutoRouter', '/DRMAttendanceForm', '/DRMFeedbackForm', '/DRMThankYou', '/DRMfeedbackForm'];
    if (publicDRMRoutes.includes(location.pathname)) return;
    if (!userCode || (!isAuthenticated && !isSSOAuthenticated)) return;

    const now = new Date();
    const month = now.getMonth() + 1;
    const year = now.getFullYear();
    const isUploader = ["SH3", "SH4", "SH5", "SH6"].includes(userType);

    dispatch(fetchNotificationsStart());

    const oaapNotifPromise = getNotificationsApi(userCode);
    const oaapCompliancePromise = isUploader
      ? getComplianceStatusApi({ userCode, role: userType, division: userDivision, month, year })
      : Promise.resolve(null);
    const jcNotifPromise = getJcNotificationsApi(userCode);
    const jcCompliancePromise = isUploader
      ? getJcComplianceStatusApi({ userCode, role: userType, division: userDivision, month, year })
      : Promise.resolve(null);

    const buildComplianceNotification = ({ complianceRes, module, label, syntheticId }) => {
      if (!complianceRes) return null;
      const c = complianceRes.data;
      if (!c || c.submitted) return null;
      let message = null;
      let type = "compliance";
      if (c.isOverdue) {
        message = `${label} submission overdue by ${c.daysOverdue} day${c.daysOverdue !== 1 ? "s" : ""}`;
        type = "compliance_overdue";
      } else if (c.daysUntilDue <= 3) {
        message = `${label} submission due in ${c.daysUntilDue} day${c.daysUntilDue !== 1 ? "s" : ""}`;
        type = "compliance_due_soon";
      }
      if (!message) return null;
      return { id: syntheticId, type, message, is_read: false, created_at: new Date().toISOString(), module };
    };

    Promise.all([oaapNotifPromise, oaapCompliancePromise, jcNotifPromise, jcCompliancePromise])
      .then(([oaapNotifRes, oaapComplianceRes, jcNotifRes, jcComplianceRes]) => {
        const oaapNotifications = (oaapNotifRes?.data || []).map((n) => ({ ...n, module: "oaap" }));
        const jcNotifications = (jcNotifRes?.data || []).map((n) => ({ ...n, module: "jc" }));

        const oaapCompliance = buildComplianceNotification({
          complianceRes: oaapComplianceRes, module: "oaap", label: "OAAP", syntheticId: "compliance_current_oaap",
        });
        const jcCompliance = buildComplianceNotification({
          complianceRes: jcComplianceRes, module: "jc", label: "BM-TSI JC Report", syntheticId: "compliance_current_jc",
        });

        const notifications = [...oaapNotifications, ...jcNotifications];
        if (jcCompliance && !notifications.some((n) => n.id === jcCompliance.id)) notifications.unshift(jcCompliance);
        if (oaapCompliance && !notifications.some((n) => n.id === oaapCompliance.id)) notifications.unshift(oaapCompliance);

        dispatch(fetchNotificationsSuccess(notifications));
      })
      .catch(() => dispatch(fetchNotificationsFailure(null)));
  }, [dispatch, userCode, userType, userDivision, isAuthenticated, isSSOAuthenticated]);

  // Check if master data is stale
  const isStale = useCallback(() => {
    const TTL = 10 * 60 * 1000; // 10 minutes
    return !lastFetchedAt || Date.now() - lastFetchedAt > TTL;
  }, [lastFetchedAt]);

  // Version check and auto-logout on version change
  useEffect(() => {
    const currentVersion = localStorage.getItem("mconnectVersion");
    if (currentVersion) {
      if (currentVersion < packageJson.version) {
        localStorage.setItem("mconnectVersion", packageJson.version);
        logout(true, packageJson.version);
      }
    } else {
      localStorage.setItem("mconnectVersion", packageJson.version);
      logout(true, packageJson.version);
    }
  }, [logout]);

  // Set auth token when available
  useEffect(() => {
    const publicDRMRoutes = ['/login', '/DRMAutoRouter', '/DRMAttendanceForm', '/DRMFeedbackForm', '/DRMThankYou', '/DRMfeedbackForm'];

    // Skip for public/auth routes — and CLEAR any stale auth header so login requests go out clean
    if (publicDRMRoutes.includes(location.pathname)) {
      setAuthToken(null);
      return;
    }

    if (accessToken) {
      setAuthToken(accessToken);
    }
  }, [accessToken, location.pathname]);

  // Refetch master data when app becomes visible and data is stale
  useEffect(() => {
    const publicDRMRoutes = ['/login', '/DRMAutoRouter', '/DRMAttendanceForm', '/DRMFeedbackForm', '/DRMThankYou', '/DRMfeedbackForm'];

    const onVisible = () => {
      // Skip for public/auth routes
      if (publicDRMRoutes.includes(window.location.pathname)) {
        return;
      }

      if (
        document.visibilityState === "visible" &&
        isAuthenticated &&
        isStale()
      ) {
        dispatch(fetchAllMasters());
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isAuthenticated, dispatch, isStale]);

  // Token refresh is now handled automatically by API interceptor on 401/403 errors
  // No periodic refresh needed - refresh happens on-demand when API calls fail

  return (
    <SnackbarProvider
      maxSnacks={3}
      autoHideDuration={4000}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      preventDuplicate
    >
      <NavigationProvider>
        <ErrorBoundary>
          <Sidebar />
          <OnlineStatus />
          <Alert />

          <Routes>
            <Route
              path="/login"
              exact
              element={<PrivateRoute component={Login} />}
            />
            <Route path="/" element={<PrivateRoute component={Homepage} />} />
            <Route
              path="/division-group"
              exact
              element={<PrivateRoute component={DivisionGroup} />}
            />
            <Route
              path="/monthly-tour-plan"
              exact
              element={<PrivateRoute component={MonthlyTourPlan} />}
            />
            {/* <Route
          path="/DRMList"
          exact
          element={<PrivateRoute component={DRMList} />}
        /> */}
            <Route
              path="/my-dealer"
              exact
              element={<PrivateRoute component={MyDealer} />}
            />
            <Route
              path="/queries"
              exact
              element={<PrivateRoute component={Queries} />}
            />

            <Route
              path="/my-diary"
              exact
              element={<PrivateRoute component={MyDiary} />}
            />
            <Route
              path="/my-team"
              exact
              element={<PrivateRoute component={MyTeam} />}
            />

            <Route
              path="/dealer-locator"
              exact
              element={<PrivateRoute component={DealerLocator} />}
            />

            <Route
              path="/pbi-dashboard"
              exact
              element={<PrivateRoute component={PbiDashboard} />}
            />
            <Route
              path="/current-day-tour"
              exact
              element={<PrivateRoute component={CurrentDayTour} />}
            />
            <Route
              path="/bottom-navbar"
              exact
              element={<PrivateRoute component={BottomNavbar} />}
            />
            <Route
              path="/key-information"
              exact
              element={<PrivateRoute component={KeyInformation} />}
            />
            <Route
              path="/total-feedback"
              exact
              element={<PrivateRoute component={TotalFeedback} />}
            />
            <Route
              path="/market-working"
              exact
              element={<PrivateRoute component={MarketWorking} />}
            />
            {/* <Route
          path="/tour-report"
          exact
          element={<PrivateRoute component={TourReport} />}
        /> */}

            <Route
              path="/exception-dealer"
              exact
              element={<PrivateRoute component={ExceptionDealer} />}
            />

            <Route
              path="/survey"
              exact
              element={<PrivateRoute component={Survey} />}
            />
            <Route
              path="/cwp"
              exact
              element={<PrivateRoute component={CWP} />}
            />
            <Route
              path="/power-bi"
              exact
              element={<PrivateRoute component={PowerBi} />}
            />
            <Route
              path="/past-sr-insight"
              exact
              element={<PrivateRoute component={PastSrInsights} />}
            />
            <Route
              path="/query-details/:id"
              exact
              element={<PrivateRoute component={QueryDetails} />}
            />
            <Route
              path="/market-visit-report"
              exact
              element={<PrivateRoute component={MarketVisitReport} />}
            />
            <Route
              path="/my-user"
              exact
              element={<PrivateRoute component={MyUser} />}
            />
            <Route
              path="/my-profile"
              exact
              element={<PrivateRoute component={MyProfile} />}
            />
            <Route
              path="/my-wss"
              exact
              element={<PrivateRoute component={MyWssPage} />}
            />
            <Route
              path="/pil-Surveys"
              exact
              element={<PrivateRoute component={PILSurveyPage} />}
            />
            <Route
              path="/oaap"
              exact
              element={<PrivateRoute component={OAAPPage} />}
            />
            <Route
              path="/jc-review"
              exact
              element={<PrivateRoute component={JcReviewPage} />}
            />
            <Route
              path="/lcdform"
              exact
              element={<PrivateRoute component={LCDForm} />}
            />
            <Route
              path="/DRM-Summary"
              exact
              element={<PrivateRoute component={DRM} />}
            />
            <Route
              path="/DRMplanning"
              exact
              element={<PrivateRoute component={DRMplanning} />}
            />
            <Route
              path="/PCMS/:id"
              exact
              element={<PrivateRoute component={PCMS} />}
            />
            <Route
              path="/PCMS"
              exact
              element={<PrivateRoute component={PCMS} />}
            />
            <Route
              path="/branch-performance-index"
              exact
              element={<PrivateRoute component={BranchPerformanceIndex} />}
            />
            <Route
              path="/tsi-market-working"
              exact
              element={<PrivateRoute component={TSIMarketWorking} />}
            />
            <Route
              path="/distributor-invoicing"
              exact
              element={<PrivateRoute component={DistributorInvoicing} />}
            />
            <Route
              path="/key-dealer-performance"
              exact
              element={<PrivateRoute component={KeyDealerPerformance} />}
            />
            <Route
              path="/pil-similar-survey"
              exact
              element={<PrivateRoute component={PILSimilarSurvey} />}
            />
            <Route
              path="/drm-details/:id"
              exact
              element={<PrivateRoute component={DRMDetails} />}
            />
            <Route
              path="/drm-meeting-approval"
              exact
              element={<PrivateRoute component={DRMMeetingApproval} />}
            />
            <Route
              path="/drm-meeting"
              exact
              element={<PrivateRoute component={DRMplanning} />}
            />
            <Route
              path="/drm-planning"
              exact
              element={<PrivateRoute component={DRM} />}
            />
            <Route path="/DRMfeedbackForm" exact element={<FeedbackForm />} />
            <Route path="/DRMAutoRouter" exact element={<DRMAutoRouter />} />
            <Route path="/DRMAttendanceForm" exact element={<AttendanceForm />} />
            <Route path="/DRMFeedbackForm" exact element={<FeedbackForm />} />
            <Route path="/DRMThankYou" exact element={<DRMThankYou />} />
          </Routes>

          {(isSSOAuthenticated || isAuthenticated) &&
           !['/login', '/DRMAutoRouter', '/DRMAttendanceForm', '/DRMFeedbackForm', '/DRMThankYou', '/DRMfeedbackForm'].includes(location.pathname) &&
           <BottomNavbar />}

          {/* Global OAAP notification drawer — available on all pages */}
          <NotificationDrawerContainer />

          {showDivisionGroupModal && (
            <DivisionGroup
              setIsDivisionGroupClicked={(value) => {
                if (!value) {
                  dispatch({ type: "HIDE_DIVISION_GROUP_MODAL" });
                }
              }}
            />
          )}
        </ErrorBoundary>
      </NavigationProvider>
    </SnackbarProvider>
  );
}

export default connect(null, { logout })(App);
